import {useMemo} from 'react';
import {MonitorUp, Phone, Users, Video} from 'lucide-react';
import {useIntl} from 'react-intl';
import {PhoneHQLoader} from 'ch3-ui-lib';
import {useMatrixContext} from '../../providers/MatrixProvider';
import {useFetchUsersQuery, type UserElement} from '../../store/features/Users/userService';
import {useFetchGroupCallSessionsQuery} from '../../features/groupCalls/services/groupCallHistoryService';
import type {GroupCallHistorySession} from '../../features/groupCalls/services/groupCallHistoryTypes';
import {
    describeGroupCallParticipants,
    formatGroupCallDuration,
    groupCallHistoryMedia,
    groupCallUsedScreenShare,
    groupSessionsByDay,
    isGroupCallInProgress,
    uniqueParticipantIds,
} from '../../features/groupCalls/services/GroupCallHistoryPresentation';
import {groupCallMessages as m} from '../../features/groupCalls/messages';
import {ParticipantAvatarStack} from '../../features/groupCalls/components/ParticipantAvatarStack';

const HISTORY_PAGE_SIZE = 100;

const fullName = (user: UserElement | undefined): string | undefined => {
    const name = [user?.firstName, user?.lastName].filter(part => !!part?.trim()).join(' ');
    return name || undefined;
};

const GroupCallHistoryRow = ({
    session,
    roomName,
    users,
}: {
    session: GroupCallHistorySession;
    roomName: string;
    users: ReadonlyMap<number, UserElement>;
}) => {
    const intl = useIntl();
    const media = groupCallHistoryMedia(session);
    const MediaIcon = media === 'video' ? Video : Phone;
    const participants = uniqueParticipantIds(session).map(userId => {
        const user = users.get(userId);
        return {displayName: fullName(user) ?? intl.formatMessage(m.historyUnknownUser), avatarUrl: user?.avatarUrl};
    });
    const starter = session.startedByUserId !== null ? fullName(users.get(session.startedByUserId)) : undefined;
    const startedAt = new Date(session.startedAt);
    const inProgress = isGroupCallInProgress(session);
    const chip = 'inline-flex h-5 shrink-0 items-center gap-1 rounded-md px-1.5 text-caption font-medium';
    // Design System ListRow: 32px tile, name + chips on one line, a single
    // truncating subline, faces and duration/time trailing. Nothing may wrap.
    return (
        <article className="flex min-h-12 items-center gap-3 rounded-control px-2 py-1.5 hover:bg-surface-muted" data-testid="group-call-history-row">
            <span className={`inline-flex size-8 shrink-0 items-center justify-center rounded-control ${inProgress ? 'bg-success-tint text-success-ink' : 'bg-brand-tint-subtle text-brand-ink'}`}>
                <MediaIcon size={16} aria-hidden="true" className="!p-0 !text-current"/>
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex min-w-0 items-center gap-2">
                    <h3 className="m-0 min-w-0 truncate text-callout font-semibold text-ink">{roomName}</h3>
                    {inProgress && (
                        <span className={`${chip} bg-success-tint text-success-ink`}>
                            {intl.formatMessage(m.historyInProgress)}
                        </span>
                    )}
                    <span className={`${chip} bg-surface-muted text-ink-secondary`}>
                        {intl.formatMessage(media === 'video' ? m.historyVideo : m.historyAudio)}
                    </span>
                    {groupCallUsedScreenShare(session) && (
                        <span className={`${chip} bg-surface-muted text-ink-secondary`}>
                            <MonitorUp size={12} aria-hidden="true" className="!p-0 !text-current"/>
                            {intl.formatMessage(m.historyScreenShare)}
                        </span>
                    )}
                </div>
                <p className="m-0 mt-0.5 truncate text-footnote text-ink-muted">
                    {starter ? `${intl.formatMessage(m.historyStartedBy, {name: starter})} · ` : ''}
                    {describeGroupCallParticipants(session)}
                </p>
            </div>
            {participants.length > 0 ? (
                <ParticipantAvatarStack participants={participants} compact/>
            ) : (
                <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-md bg-surface-muted text-ink-muted">
                    <Users size={12} aria-hidden="true" className="!p-0 !text-current"/>
                </span>
            )}
            <div className="flex shrink-0 flex-col items-end">
                <span className={`text-footnote font-medium ${inProgress ? 'text-success-ink' : 'text-ink'}`}>{formatGroupCallDuration(session)}</span>
                <span className="text-caption text-ink-muted">
                    {Number.isNaN(startedAt.getTime()) ? '' : intl.formatTime(startedAt, {hour: 'numeric', minute: '2-digit'})}
                </span>
            </div>
        </article>
    );
};

const GroupCallHistory = () => {
    const intl = useIntl();
    const {rooms} = useMatrixContext();
    const {data: userList = []} = useFetchUsersQuery({});
    const {data, isLoading, isError} = useFetchGroupCallSessionsQuery(
        {page: 0, size: HISTORY_PAGE_SIZE},
        {pollingInterval: 30_000},
    );
    const roomNames = useMemo(() => new Map(rooms.map(room => [room.roomId, room.name])), [rooms]);
    const users = useMemo(() => {
        const byId = new Map<number, UserElement>();
        userList.forEach(user => {
            byId.set(user.userId ?? user.id, user);
        });
        return byId;
    }, [userList]);
    const days = useMemo(() => groupSessionsByDay(data?.sessions ?? []), [data?.sessions]);

    if (isLoading) {
        return <div className="flex h-full items-center justify-center"><PhoneHQLoader/></div>;
    }
    if (isError) {
        return (
            <div className="flex h-full items-center justify-center p-8 text-center">
                <div>
                    <h2 className="text-base font-semibold text-zinc-900">{intl.formatMessage(m.historyErrorTitle)}</h2>
                    <p className="mt-2 text-sm text-zinc-500">{intl.formatMessage(m.historyErrorBody)}</p>
                </div>
            </div>
        );
    }
    if (days.length === 0) {
        return (
            <div className="flex h-full items-center justify-center p-8 text-center" data-testid="group-call-history-empty">
                <div>
                    <Users className="mx-auto text-zinc-400" size={40} aria-hidden="true"/>
                    <h2 className="mt-4 text-base font-semibold text-zinc-900">{intl.formatMessage(m.historyEmptyTitle)}</h2>
                    <p className="mt-2 text-sm text-zinc-500">{intl.formatMessage(m.historyEmptyBody)}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-63px-var(--header-height))] overflow-y-auto px-2 pb-3" data-testid="group-call-history">
            {days.map(({day, sessions}) => (
                <section key={day} aria-label={day}>
                    <h2 className="m-0 px-2 pb-1 pt-3 text-caption font-semibold text-ink-muted">
                        {day === 'unknown'
                            ? day
                            : intl.formatDate(new Date(`${day}T00:00:00`), {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})}
                    </h2>
                    {sessions.map(session => (
                        <GroupCallHistoryRow
                            key={session.sessionId}
                            session={session}
                            roomName={roomNames.get(session.roomId) || intl.formatMessage(m.historyUnknownRoom)}
                            users={users}
                        />
                    ))}
                </section>
            ))}
            {data && data.totalCount > data.sessions.length && (
                <p className="py-4 text-center text-xs text-zinc-500">
                    {intl.formatMessage(m.historyShowing, {shown: data.sessions.length, total: data.totalCount})}
                </p>
            )}
        </div>
    );
};

export default GroupCallHistory;
